/**
 * MarkdownToolbar — a compact formatting bar for markdown textareas.
 *
 * Provides Bold, Italic, and Link insertion. The Link button opens a small
 * inline popover where the editor enters the URL (and optional anchor text),
 * then inserts `[anchor](url)` at the cursor / around the selection.
 *
 * Usage:
 *   const ref = useRef<HTMLTextAreaElement>(null);
 *   <MarkdownToolbar textareaRef={ref} onChange={(v) => setVal(v)} value={val} />
 *   <textarea ref={ref} value={val} onChange={...} />
 */
import { Bold, Italic, Link2 } from 'lucide-react';
import React, { useRef, useState } from 'react';

interface Props {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (newValue: string) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function insertWrap(
  textarea: HTMLTextAreaElement,
  value: string,
  before: string,
  after: string,
): string {
  const { selectionStart: ss, selectionEnd: se } = textarea;
  const selected = value.slice(ss, se) || 'text';
  const newVal = value.slice(0, ss) + before + selected + after + value.slice(se);
  // Restore focus + selection after React re-renders
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(ss + before.length, ss + before.length + selected.length);
  });
  return newVal;
}

function insertLink(
  textarea: HTMLTextAreaElement,
  value: string,
  url: string,
  anchor: string,
): string {
  const { selectionStart: ss, selectionEnd: se } = textarea;
  const text = anchor || value.slice(ss, se) || 'link';
  const md = `[${text}](${url})`;
  const newVal = value.slice(0, ss) + md + value.slice(se);
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(ss, ss + md.length);
  });
  return newVal;
}

// ── Toolbar button ────────────────────────────────────────────────────────────

const TBtn: React.FC<{
  title: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ title, active, onClick, children }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className="ds-btn ds-btn-ghost ds-btn-sm w-7 h-7 p-0"
    style={active ? { color: 'var(--ds-brand)', background: 'var(--ds-brand-soft)' } : undefined}
  >
    {children}
  </button>
);

// ── Main component ────────────────────────────────────────────────────────────

export const MarkdownToolbar: React.FC<Props> = ({ textareaRef, value, onChange }) => {
  const [showLinkPopover, setShowLinkPopover] = useState(false);
  const [linkUrl, setLinkUrl] = useState('https://');
  const [linkAnchor, setLinkAnchor] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  const ta = () => textareaRef.current;

  const handleBold = () => {
    const el = ta();
    if (!el) return;
    onChange(insertWrap(el, value, '**', '**'));
  };

  const handleItalic = () => {
    const el = ta();
    if (!el) return;
    onChange(insertWrap(el, value, '_', '_'));
  };

  const openLinkPopover = () => {
    const el = ta();
    // Pre-fill anchor with current selection
    if (el) {
      const sel = value.slice(el.selectionStart, el.selectionEnd);
      setLinkAnchor(sel);
    }
    setLinkUrl('https://');
    setShowLinkPopover(true);
  };

  const handleInsertLink = () => {
    const el = ta();
    if (!el || !linkUrl.trim() || linkUrl === 'https://') return;
    onChange(insertLink(el, value, linkUrl.trim(), linkAnchor.trim()));
    setShowLinkPopover(false);
    setLinkAnchor('');
    setLinkUrl('https://');
  };

  return (
    <div className="relative">
      {/* Toolbar row */}
      <div className="flex items-center gap-0.5 px-1.5 py-1 rounded-t-lg border border-b-0"
        style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}>
        <TBtn title="Bold (** text **)" onClick={handleBold}>
          <Bold className="w-3.5 h-3.5" />
        </TBtn>
        <TBtn title="Italic (_ text _)" onClick={handleItalic}>
          <Italic className="w-3.5 h-3.5" />
        </TBtn>

        {/* divider */}
        <span className="w-px h-4 mx-1 shrink-0" style={{ background: 'var(--ds-divider)' }} />

        <TBtn title="Insert link" active={showLinkPopover} onClick={openLinkPopover}>
          <Link2 className="w-3.5 h-3.5" />
        </TBtn>

        <span className="ml-auto t-tag t-muted pr-1 hidden sm:block">
          Markdown supported
        </span>
      </div>

      {/* Link popover */}
      {showLinkPopover && (
        <div
          ref={popoverRef}
          className="ds-card-pop absolute left-0 top-full mt-1 z-30 w-72 flex flex-col gap-2"
        >
          <p className="t-card-title">Insert link</p>

          <div className="flex flex-col gap-1">
            <label className="t-label">URL *</label>
            <input
              autoFocus
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInsertLink(); } if (e.key === 'Escape') setShowLinkPopover(false); }}
              placeholder="https://example.com"
              className="ds-input ds-input-sm"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="t-label">Anchor text (optional)</label>
            <input
              value={linkAnchor}
              onChange={(e) => setLinkAnchor(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInsertLink(); } if (e.key === 'Escape') setShowLinkPopover(false); }}
              placeholder="Leave blank to use selected text"
              className="ds-input ds-input-sm"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={handleInsertLink}
              disabled={!linkUrl.trim() || linkUrl === 'https://'}
              className="ds-btn ds-btn-primary ds-btn-sm flex-1"
            >
              Insert
            </button>
            <button
              type="button"
              onClick={() => setShowLinkPopover(false)}
              className="ds-btn ds-btn-secondary ds-btn-sm flex-1"
            >
              Cancel
            </button>
          </div>

          <p className="t-tag t-muted">
            Tip: select text before clicking Link to auto-fill the anchor.
          </p>
        </div>
      )}
    </div>
  );
};
